import * as React from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { InfoAlert } from '@amzn/dvui/dist/Alert'
import { Text } from '@amzn/dvui/dist/Text'
import { StringId } from '../localizer/stringId'
import { IGetString } from '../../utils/stringUtils'
import * as st from './refund.module.scss'
import * as stUtility from '../utilityClasses.module.scss'
import { withLocalization, LocalizerProps } from '../localizer/localizer'
import { IDetailPageState } from '../../reducers'
import { sanitizedRequestContextSelector } from '../../selectors/requestContext'
import { triggerRefund } from '../../actions/refund'
import { withLogger, ILogger } from '../../context/withLogger'

const refundMultipleNoJSLink = '/digital-orders/ref=atv_dp_core_cancel'
const refundModalPath =
  '/gp/video/widgets/self-service-order-cancel-box/ajax' +
  '/multiple-orders.html/ref=atv_dp_can_ord_modal'

const refundModalLink = (asins: string[]) =>
  `${refundModalPath}?ie=UTF8&asins=${(asins.length && asins.join(',')) ||
    asins}`

const refundModalId = (refundIds: string[] = []) =>
  ['dv_self_cancel', ...refundIds].join('_')

type StateProps = ReturnType<typeof mapStateToProps>
type DispatchProps = ReturnType<typeof mapDispatchToProps>
type OwnProps = {
  titleId: string
}
export type Props = StateProps & DispatchProps & OwnProps & ILogger

const refundModalAttrs = (refundIds: string[], getString: IGetString) => ({
  width: 540,
  name: 'refund-order',
  hideHeader: 0,
  showCloseButton: 1,
  buttons: [
    {
      customEvent: 'cancel-order:dv-self-cancel-modal:submit',
      text: getString(StringId.DV_RBB_CANCEL_PURCH_MODAL_SUBMIT),
      id: refundModalId(refundIds),
      type: 'submit'
    }
  ],
  url: refundModalLink(refundIds),
  header: {
    text: getString(StringId.DV_RBB_CANCEL_PURCH_MODAL_HEADER)
  }
})

const createCancelLinkText = (
  href: string,
  getString: IGetString,
  additionalAttr?: { [index: string]: string }
) => ({
  string: getString(StringId.DV_AB_CANCEL_ACCIDENTAL_PURCHASE),
  attrs: { cancelLink: { href, ...additionalAttr } }
})

type CancelLinkLegacy = {
  refundableOrders: WpsRuntime.atv.wps.RefundableOrder[]
} & LocalizerProps

const CancelLinkLegacy = withLocalization(
  ({ refundableOrders, getString }: CancelLinkLegacy) => {
    const refundIds = refundableOrders.map(order => order.asin)
    const multiRefundAttrs = {
      class: 'a-declarative',
      'data-action': 'aiv-modal',
      'data-aiv-modal': JSON.stringify(refundModalAttrs(refundIds, getString)),
      name: 'refund-order'
    }
    const cancelLinkText =
      refundableOrders.length === 1
        ? createCancelLinkText(refundableOrders[0].singleRefundLink, getString)
        : createCancelLinkText(
            refundMultipleNoJSLink,
            getString,
            multiRefundAttrs
          )

    return <Text string={cancelLinkText} />
  }
)

type CancelLink = {
  triggerRefund: () => void
} & LocalizerProps

const CancelLink = withLocalization(
  ({ triggerRefund, getString }: CancelLink) => {
    const dvString = getString(StringId.DV_AB_CANCEL_ACCIDENTAL_PURCHASE)
    const linkStartTag = '{link|cancelLink}'
    const linkEndTag = '{end}'
    const linkStartIndex = dvString.indexOf(linkStartTag)
    const linkEndIndex = dvString.indexOf(linkEndTag)
    const prefix = dvString.substring(0, linkStartIndex)
    const link = dvString.substring(
      linkStartIndex + linkStartTag.length,
      linkEndIndex
    )
    const suffix = dvString.substring(linkEndIndex + linkEndTag.length)
    return (
      <>
        {prefix && <span>{prefix}</span>}
        <a href='#' onClick={() => triggerRefund()}>
          {link}
        </a>
        {suffix && <span>{suffix}</span>}
      </>
    )
  }
)

export const ConnectedRefund = ({
  refundFragment,
  isRetail,
  triggerRefund
}: Props) => {
  if (
    !refundFragment ||
    !refundFragment.refundableOrders ||
    refundFragment.refundableOrders.length === 0
  ) {
    return null
  }

  return (
    <div className={`${st.refundLink} ${stUtility.fullWidth} refund-alert`}>
      <InfoAlert>
        {isRetail ? (
          <CancelLinkLegacy
            refundableOrders={refundFragment.refundableOrders}
          />
        ) : (
          <CancelLink triggerRefund={triggerRefund} />
        )}
      </InfoAlert>
    </div>
  )
}

const mapStateToProps = (state: IDetailPageState, { titleId }: OwnProps) => ({
  refundFragment: state.refund.fragments[titleId],
  isRetail: sanitizedRequestContextSelector(state).isRetail
})

const mapDispatchToProps = (
  dispatch: Dispatch,
  { logger, titleId }: ILogger & OwnProps
) => ({
  triggerRefund: () => triggerRefund(dispatch, logger, titleId)
})

export const Refund: React.ComponentType<OwnProps> = withLogger(
  connect(mapStateToProps, mapDispatchToProps)(ConnectedRefund)
)
